import { Maybe }		from "ts-base/maybe";
import { Nullable }		from "ts-base/nullable";

import { FormSchema }	from "@v3/geko/form/form";
import * as fields		from "@v3/geko/form/fields";

import * as i18n		from "@geotoura/shared/i18n";
import * as upModel		from "@geotoura/shared/upModel";
import * as commonModel	from "@geotoura/shared/commonModel";

export type ScreenOrientation	= "portrait" | "landscape";

export type ScreenSize = Readonly<{
	width:			number,
	height:			number,
	orientation:	ScreenOrientation,
	mode:			"compact"|"loose",
	canHover:		boolean,
}>;

// this is exclusively used to simulate passing an Event<Partial<MapView>> into preact by timestamping a prop.
export type SetMapViewEvent	= Readonly<{
	timestamp:	number,
	zoom:		number,
	center:		commonModel.Coordinate,
	pinVisible:	boolean,
	polygon:	Array<commonModel.Coordinate>,
}>;

export const setMapViewEvent	= (data:Omit<SetMapViewEvent, "timestamp">):SetMapViewEvent	=> ({
	timestamp:	Date.now(),
	...data,
});

export type MapView = Readonly<{
	zoom:	number,
	center:	commonModel.Coordinate,
}>;

// TODO cleanup type is identical to shared image type, redundant
// TODO a11y missing image description
export type GalleryImage = Readonly<{
	file:	string,
}>;

export type RouteStats = Readonly<{
	stations:		number,
	currentStation:	number|null,
	isFirst:		boolean,
	isLast:			boolean,
	nights:			number,
	activities:		number,
	wishes:			number,
}>;

export type GalleryState = Readonly<{
	open:	boolean,
	index:	number,
}>;

export type DestinationState = Readonly<{
	destinationGallery:	GalleryState,
	hotelGallery:		GalleryState,
	sightsOpen:			boolean,
	wishOpen:			boolean,
	wishTmp:			string,
}>;

export type DrawerMode	= Readonly<{
	open:		boolean,
	content:	"destination"|"route",
}>;

export type DrawerState = DrawerMode & Readonly<{
	// true while the open state has changed and the transitionend event on .app-content has not fired yet
	transition:	boolean,
}>;

export type Model = Readonly<{
	screenSize:				ScreenSize,
	destination:			upModel.DesktopDestination|null,
	route:					Route,
	savedRoute:				ReadonlyArray<upModel.RouteItem>,
	viewRegions:			boolean,
	modalHelp:				boolean,
	modalHowto:				boolean,
	modalHowtoRegionIds:	ReadonlyArray<commonModel.RegionId>,
	modalOverwriteRoute:	boolean,
	modalRequest:			boolean,
	modalRouteDelete:		boolean,
	destinationState:		DestinationState,
	drawerState:			DrawerState,
	mapDefaults:			MapView,
	setMapViewEvent:		SetMapViewEvent|null,
	entryPoint:				upModel.EntryPoint,
	warnings:				ReadonlyArray<string>,
	exampleRouteData:		ExampleRouteModel,
	personalData:			PersonalModel,
	flightEnabled:			boolean,
	flightData:				FlightModel,
	// travelData:				upModel.TravelModel,
	requestResponse:		upModel.RequestResponse|null,
	requestEnroute:			boolean,
}>;

export namespace Model {
	export const extractFormData	= (model:Model):Maybe<FormData>	=>
		Maybe.object({
			personal:	PersonalSchema.extract(model.personalData),
			flight:		Nullable.traverseMaybe(model.flightEnabled ? model.flightData : null)(FlightSchema.extract),
		});
}

export type Route	= Readonly<{
	items:	ReadonlyArray<upModel.RouteItem>,
	index:	number,
	added:	boolean,
}>;

export type FormData	= Readonly<{
	personal:	PersonalData,
	flight:		FlightData|null,
}>;

export const PersonalSchema	=
	FormSchema.of({
		email:				fields.RequiredText(commonModel.EmailAddressLtd)(null),
		title:				fields.FreeSelect<commonModel.Title>(commonModel.Title.brand("")),
		gender:				fields.FreeSelect<commonModel.Gender|null>(null),
		firstName:			fields.FreeText(commonModel.FirstName)(commonModel.FirstName.brand("")),
		lastName:			fields.FreeText(commonModel.LastName)(commonModel.LastName.brand("")),
		duration:			fields.FreeCounter(0),
		start:				fields.OptionalIsoDate(null),
		end:				fields.OptionalIsoDate(null),
		rentalCarCategory:	fields.FreeSelect<commonModel.CarCategoryId>(commonModel.CarCategoryId.brand(2)),
		hotelCategory:		fields.FreeSelect<commonModel.HotelCategoryId>(commonModel.HotelCategoryId.brand(2)),
		nrAdults:			fields.FreeCounter(0),
		nrBabies:			fields.FreeCounter(0),
		nrKids:				fields.FreeCounter(0),
		roomSingle:			fields.FreeCounter(0),
		roomDouble:			fields.FreeCounter(0),
		kidBeds:			fields.FreeCounter(0),
		babyBeds:			fields.FreeCounter(0),
		notes:				fields.AnyText(""),
		privacy:			fields.RequiredCheckbox(false),
	});
export type PersonalModel	= FormSchema.ModelOf<typeof PersonalSchema>;
export type PersonalData	= FormSchema.ExtractOf<typeof PersonalSchema>;

export const FlightSchema	=
	FormSchema.of({
		postCode:	fields.RequiredText(commonModel.PostCode)(null),
		country:	fields.FreeSelect<i18n.CountryId>(i18n.CountryId.brand("DE")),
	});
export type FlightModel	= FormSchema.ModelOf<typeof FlightSchema>;
export type FlightData	= FormSchema.ExtractOf<typeof FlightSchema>;

export const ExampleRouteSchema	=
	FormSchema.of({
		exampleRoute:	fields.FreeRadio<commonModel.ExampleRouteId|null>(null),
	});
export type ExampleRouteModel	= FormSchema.ModelOf<typeof ExampleRouteSchema>;
export type ExampleRouteData	= FormSchema.ExtractOf<typeof ExampleRouteSchema>;
